import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/* ===== IMAGE MAP ===== */
const IMG = {
  hero: "/hero_inclusiva.png",
  torre: "/colle_torre.png",
  piazza: "/colle_piazza.png",
  porta: "/colle_porta.png",
  cascata: "/colle_cascata.png",
  torri: "/colle_torri.png",
  boomer: "/boomer_vintage.png",
  inclusiva: "/inclusiva_community.png",
  fonti: "/valdelsa_fonti.png",
  fuochi: "/valdelsa_fuochi.png",
  castello: "/valdelsa_castello.png",
  torriColori: "/valdelsa_torri_colori.png",
  sbandieratori: "/valdelsa_sbandieratori.png",
  bottegaHero: "/bottega_hero.png",
  bottegaPasta: "/bottega_pasta.png",
  bottegaArtigiano: "/bottega_artigiano.png",
  bottegaUncinetto: "/bottega_uncinetto.png",
  tuscany: "/borgo_sangimignano.png",
  sanGimignano: "/borgo_monteriggioni.png",
  tshirt: "/tshirt_genz.png",
  cover: "/cover_genz.png",
  stallions1: "/genz_stallions_1.png",
  agoraz2: "/agora_z_2.png",
  covoz3: "/covo_z_3.png",
};

/* ===== CSS ANIMATIONS ===== */
const animCSS = `
@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  14% { transform: scale(1.08); }
  28% { transform: scale(1); }
  42% { transform: scale(1.08); }
  56% { transform: scale(1); }
}
@keyframes glowPulse {
  0%, 100% { text-shadow: 0 0 20px rgba(0,255,65,0.3); }
  50% { text-shadow: 0 0 60px rgba(0,255,65,0.7), 0 0 120px rgba(0,255,65,0.3); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes scanline {
  0% { top: -10%; }
  100% { top: 110%; }
}
`;

/* ===== FULL-WIDTH IMAGE SECTION ===== */
const ImgSection = ({ src, alt, children, overlay = 'bg-black/60', height = 'min-h-[50vh]', testId }) => (
  <div className={`relative ${height} overflow-hidden`} data-testid={testId}>
    <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className={`absolute inset-0 ${overlay}`} />
    <div className="relative z-10 h-full flex items-center">
      {children}
    </div>
  </div>
);

/* ===== SECTION DIVIDER ===== */
const Divider = ({ color = '#00FF41' }) => (
  <div className="flex items-center gap-4 my-12 max-w-5xl mx-auto px-4">
    <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}40, transparent)` }} />
    <span className="font-mono text-xs tracking-[0.3em] uppercase" style={{ color: `${color}80` }}>&lt;/&gt;</span>
    <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}40, transparent)` }} />
  </div>
);

/* ===== SHIELD CARD ===== */
const ShieldCard = ({ icon, title, subtitle, color, active, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-center cursor-pointer ${active ? 'scale-105' : 'hover:scale-[1.02]'}`}
    style={{
      borderColor: active ? color : '#27272a',
      background: active ? `${color}10` : '#0A0A0A',
      boxShadow: active ? `0 0 30px ${color}20` : 'none',
    }}
  >
    <span className="text-3xl block mb-1">{icon}</span>
    <h3 className="font-mono font-black text-sm" style={{ color: active ? color : '#d4d4d8' }}>{title}</h3>
    <p className="text-zinc-500 text-[10px] font-mono mt-0.5">{subtitle}</p>
  </button>
);

/* ============================== */
/* ===== MAIN COMPONENT ===== */
/* ============================== */
const DoposcuolaPage = () => {
  const [activeShield, setActiveShield] = useState(null);

  const shields = [
    { id: 'genz', icon: '\u2694', title: 'Agora GenZ', subtitle: 'Teenager della Valdelsa', color: '#FFD700' },
    { id: 'bottega', icon: '\uD83C\uDFFA', title: 'La Bottega', subtitle: 'Le Arti Perdute', color: '#D4A574' },
    { id: 'boomer', icon: '\u2615', title: 'Boomer & GenX', subtitle: 'Nati nel boom', color: '#CD7F32' },
    { id: 'senatori', icon: '\u265F', title: 'I Senatori', subtitle: 'Over 70', color: '#C0C0C0' },
    { id: 'inclusivo', icon: '\u2764', title: 'Spazio Inclusivo', subtitle: 'Nessuno escluso', color: '#E040FB' },
    { id: 'sport', icon: '\u26BD', title: 'Sport per Tutti', subtitle: 'Ogni sera', color: '#FF6B35' },
  ];

  const isVisible = (id) => !activeShield || activeShield === id;

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-20">
      <style>{animCSS}</style>

      {/* ======================================== */}
      {/* HERO — FULL-WIDTH INCLUSIVA */}
      {/* ======================================== */}
      <ImgSection
        src={IMG.hero}
        alt="Comunita inclusiva della Valdelsa"
        overlay="bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20"
        height="min-h-[75vh]"
        testId="hero-section"
      >
        <div className="w-full text-center px-4 mt-auto pb-14">
          <div className="mb-4" style={{ animation: 'heartbeat 2s ease-in-out infinite' }}>
            <span className="font-mono font-black text-[#00FF41] text-lg sm:text-xl" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
              &lt;/AI_covo.tech&gt;&reg;
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-mono text-white mb-3 leading-tight"
            style={{ animation: 'fadeInUp 1s ease-out', textShadow: '0 2px 30px rgba(0,0,0,0.8)' }}>
            IL CIRCOLO RICREATIVO<br />
            <span className="text-[#00FF41]" style={{ textShadow: '0 0 40px rgba(0,255,65,0.5)' }}>CULTURALE 4.0</span>
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto font-mono" style={{ animation: 'fadeInUp 1.2s ease-out' }}>
            L'Agora digitale della Valdelsa dove ogni generazione trova il suo posto.
          </p>
        </div>
      </ImgSection>

      {/* ======================================== */}
      {/* MANIFESTO — TORRE BACKGROUND */}
      {/* ======================================== */}
      <ImgSection src={IMG.torre} alt="Torre della Valdelsa" overlay="bg-black/65" height="min-h-[50vh]" testId="manifesto-section">
        <div className="w-full text-center px-6 py-12">
          <p className="text-[#00FF41] font-mono font-black text-2xl md:text-4xl mb-6" style={{ textShadow: '0 0 30px rgba(0,255,65,0.4)' }}>
            "Non hanno un posto dove andare."
          </p>
          <p className="text-white/90 max-w-2xl mx-auto text-sm md:text-base leading-relaxed mb-3">
            Ogni pomeriggio, decine di ragazzi della Valdelsa si ritrovano sotto la banca rossa. O sotto l'arco del supermarket, al freddo. Non per scelta.
          </p>
          <p className="text-white/90 max-w-2xl mx-auto text-sm md:text-base leading-relaxed mb-4">
            I nonni giocano a carte nel bar, ma non hanno piu un circolo. Chi ha una disabilita, la solitudine — non ha uno spazio che lo accolga senza giudizio.
          </p>
          <p className="text-[#00FF41] font-mono font-black text-xl md:text-2xl" style={{ textShadow: '0 0 20px rgba(0,255,65,0.4)' }}>
            Fino ad oggi.
          </p>
        </div>
      </ImgSection>

      {/* ======================================== */}
      {/* TUSCANY PANORAMA BREAK */}
      {/* ======================================== */}
      <ImgSection src={IMG.tuscany} alt="San Gimignano al tramonto" overlay="bg-black/30" height="min-h-[35vh]" testId="tuscany-break">
        <div className="w-full text-center px-4">
          <p className="text-white font-mono font-black text-xl md:text-3xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            San Gimignano. Monteriggioni. Casole. Poggibonsi. Colle.<br/>La Valdelsa. La nostra casa.
          </p>
        </div>
      </ImgSection>

      {/* ======================================== */}
      {/* LE 6 ANIME DEL COVO */}
      {/* ======================================== */}
      <div className="max-w-5xl mx-auto px-4 py-16" data-testid="shield-selector">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-black font-mono text-white mb-2">
            Le <span className="text-[#00FF41]">6 Anime</span> del Covo
          </h2>
          <p className="text-zinc-400 font-mono text-sm max-w-xl mx-auto">
            Un solo tetto. Sei mondi. Ognuno trova il suo spazio.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {shields.map(s => (
            <ShieldCard
              key={s.id} testId={`shield-${s.id}`}
              icon={s.icon} title={s.title} subtitle={s.subtitle} color={s.color}
              active={activeShield === s.id}
              onClick={() => setActiveShield(activeShield === s.id ? null : s.id)}
            />
          ))}
        </div>
      </div>

      {/* ======================================== */}
      {/* SEZIONE 1: AGORA GENZ */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('genz') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-genz">
        {/* Full-width sbandieratori */}
        <ImgSection src={IMG.sbandieratori} alt="Sbandieratori nella piazza" overlay="bg-gradient-to-r from-black/80 via-black/50 to-transparent" height="min-h-[45vh]">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <span className="text-3xl block mb-2">{'\u2694'}</span>
            <h2 className="text-3xl md:text-4xl font-black font-mono text-yellow-400 mb-3" style={{ textShadow: '0 0 15px rgba(255,215,0,0.3)' }}>
              Agora GenZ
            </h2>
            <p className="text-zinc-300 font-mono text-xs uppercase tracking-widest mb-4">Il Rione dei Teenager — Valdelsa</p>
            <p className="text-white/80 max-w-lg text-sm leading-relaxed">
              Come i Rioni delle citta toscane, con gli sbandieratori e il Palio — ma per la generazione Z.
              Sfide, tornei, classifica live, merchandising esclusivo. La tradizione incontra il futuro.
            </p>
          </div>
        </ImgSection>

        <div className="max-w-5xl mx-auto px-4 py-12">
          {/* Orari cards con piazza background */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="relative rounded-xl overflow-hidden group" data-testid="card-doposcuola">
              <img src={IMG.piazza} alt="Piazza della Valdelsa" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="text-yellow-400 font-mono font-black text-3xl block mb-1">15:00 — 18:00</span>
                <span className="text-white font-black text-xl">DOPOSCUOLA</span>
                <p className="text-zinc-300 text-sm mt-1">L'Agora 4.0 — studio, wifi, divani, musica.</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden group" data-testid="card-sport-genz">
              <img src={IMG.fuochi} alt="Fuochi sulla Fortezza" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="text-yellow-400 font-mono font-black text-3xl block mb-1">18:00 — 20:00</span>
                <span className="text-white font-black text-xl">SPORT</span>
                <p className="text-zinc-300 text-sm mt-1">Calcetto, basket, fitness. Niente scuse, si gioca.</p>
              </div>
            </div>
          </div>

          {/* Merch logos */}
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            <img src={IMG.stallions1} alt="GenZ Stallions" className="rounded-lg border border-yellow-500/30" />
            <img src={IMG.agoraz2} alt="Agora Z" className="rounded-lg border border-yellow-500/30" />
            <img src={IMG.covoz3} alt="Covo Z" className="rounded-lg border border-yellow-500/30" />
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SEZIONE 2: LA BOTTEGA — LE ARTI PERDUTE */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('bottega') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-bottega">
        {/* Full-width hero bottega */}
        <ImgSection src={IMG.bottegaHero} alt="Senatori e GenAlpha nella piazza" overlay="bg-gradient-to-t from-[#050505] via-black/40 to-black/20" height="min-h-[55vh]">
          <div className="w-full text-center px-6 mt-auto pb-10">
            <p className="font-mono text-xs uppercase tracking-[0.4em] mb-3" style={{ color: '#D4A574' }}>Senatori + Generazione Alpha</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-mono text-white mb-3" style={{ textShadow: '0 2px 30px rgba(0,0,0,0.8)' }}>
              LA BOTTEGA
            </h2>
            <p className="text-2xl md:text-3xl font-mono font-black mb-4" style={{ color: '#D4A574', textShadow: '0 0 20px rgba(212,165,116,0.3)' }}>
              Le Arti Perdute
            </p>
            <p className="text-white/90 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              Il passato che insegna arti perdute al presente.<br />
              Il presente che le conserva e le tramanda alla <span className="text-yellow-300 font-bold">Generazione Alpha</span>.
            </p>
          </div>
        </ImgSection>

        {/* Il messaggio potente */}
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <p className="font-mono font-black text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed">
              I Senatori hanno nelle mani <span style={{ color: '#D4A574' }}>secoli di sapere</span>.
              I bambini della Gen Alpha sono il futuro.
              <span className="block mt-2 text-[#00FF41]">
                La Bottega li mette insieme.
              </span>
            </p>
          </div>

          {/* Le 3 immagini AI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-cucina">
              <img src={IMG.bottegaPasta} alt="Nonna insegna a fare la pasta" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-mono font-black text-lg text-white mb-1">La Cucina della Nonna</h3>
                <p className="text-zinc-300 text-xs leading-relaxed">
                  Pici fatti a mano, ribollita, pappa al pomodoro. La cucina contadina toscana — povera ma ricchissima. Le nonne insegnano, i bambini imparano.
                </p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-artigiano">
              <img src={IMG.bottegaArtigiano} alt="Nonno insegna a fare i cestini" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-mono font-black text-lg text-white mb-1">L'Officina del Nonno</h3>
                <p className="text-zinc-300 text-xs leading-relaxed">
                  Cestini di vimini, cappelli di paglia, aggiustatutto. Le mani dei nonni sanno fare cose che nessun tutorial potra mai insegnare.
                </p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden group" data-testid="bottega-arti">
              <img src={IMG.bottegaUncinetto} alt="Uncinetto e arti manuali nel cortile" className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-mono font-black text-lg text-white mb-1">Il Cortile delle Arti</h3>
                <p className="text-zinc-300 text-xs leading-relaxed">
                  Uncinetto, maglia, intaglio del legno, fare la legna, andare a caccia. Le arti perdute riprendono vita nei cortili della Valdelsa.
                </p>
              </div>
            </div>
          </div>

          {/* Tags delle arti */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {['Pasta fatta a mano', 'Cestini di vimini', 'Cappelli di paglia', 'Uncinetto', 'Intaglio legno',
              'Aggiustatutto', 'Cucina contadina', 'Fare la legna', 'Andare a caccia', 'Ricamo',
              'Conserve', 'Orto', 'Potatura'].map(tag => (
              <span key={tag} className="border font-mono text-xs px-3 py-1.5 rounded-full" style={{ borderColor: '#D4A57440', color: '#D4A574', background: '#D4A57410' }}>
                {tag}
              </span>
            ))}
          </div>

          <div className="text-center p-8 border rounded-xl" style={{ borderColor: '#D4A57430', background: '#D4A57408' }} data-testid="bottega-quote">
            <p className="font-mono font-black text-lg md:text-xl text-white mb-2">
              "Ogni nonno che muore e una biblioteca che brucia."
            </p>
            <p className="text-zinc-400 text-sm mb-4">— Proverbio africano</p>
            <p className="font-mono font-bold text-sm max-w-xl mx-auto mb-6" style={{ color: '#D4A574' }}>
              La Bottega del Covo e la risposta: salviamo i libri prima che brucino. Facciamo scrivere i nuovi capitoli ai bambini.
            </p>
            <Link to="/artistigianato" data-testid="link-artistigianato" className="inline-block border-2 border-[#D4A574] text-[#D4A574] font-mono font-black text-sm px-6 py-3 rounded-lg hover:bg-[#D4A574]/10 transition-colors">
              SCOPRI ARTISTIGIANATO — Il Festival
            </Link>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SEZIONE 3: BOOMER & GENX */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('boomer') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-boomer">
        <ImgSection src={IMG.boomer} alt="Italia anni 60" overlay="bg-gradient-to-r from-black/80 via-black/50 to-transparent" height="min-h-[45vh]">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <span className="text-3xl block mb-2">{'\u2615'}</span>
            <h2 className="text-3xl md:text-4xl font-black font-mono mb-3" style={{ color: '#CD7F32', textShadow: '0 0 15px rgba(205,127,50,0.3)' }}>
              Boomer & GenX Club
            </h2>
            <p className="text-white/90 max-w-md text-sm leading-relaxed mb-3">
              Perche si chiamano Boomer? Perche sono i figli del <span className="font-bold text-yellow-300">boom economico italiano degli anni '50</span>.
              La Vespa, la FIAT 500, il bar sotto casa. Loro sono la memoria viva.
            </p>
            <p className="text-zinc-300 text-xs font-mono">GenX (1965-1980) — Il ponte tra generazioni</p>
          </div>
        </ImgSection>

        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="relative rounded-xl overflow-hidden" data-testid="article-baby-boomer">
              <img src={IMG.fonti} alt="Le Fonti della Valdelsa" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-mono font-black text-lg mb-1" style={{ color: '#CD7F32' }}>Baby Boomer (1946-1964)</h3>
                <p className="text-zinc-300 text-sm">Nati nel boom. Conoscono il valore di un circolo, di una piazza. Al Covo ritrovano quello spirito.</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden" data-testid="article-genx">
              <img src={IMG.castello} alt="Castello della Valdelsa" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-mono font-black text-lg mb-1" style={{ color: '#CD7F32' }}>Generazione X (1965-1980)</h3>
                <p className="text-zinc-300 text-sm">Cresciuti senza internet, arrivati al digitale da adulti. Il Digital Detox non e un lusso, e sopravvivenza.</p>
              </div>
            </div>
          </div>

          <div className="text-center p-8 border rounded-xl" style={{ borderColor: '#CD7F3230', background: '#CD7F3208' }} data-testid="digital-detox">
            <h3 className="text-xl font-black font-mono text-white mb-3">
              Digital <span style={{ color: '#CD7F32' }}>Detox</span> — Stacca. Respira. Vivi.
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {['Escursioni', 'Laboratori', 'Cene Comunitarie', 'Serate Racconto', 'Corsi Manuali'].map(tag => (
                <span key={tag} className="bg-[#CD7F32]/15 border border-[#CD7F32]/30 text-[#CD7F32] font-mono text-xs px-3 py-1.5 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SEZIONE 4: I SENATORI */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('senatori') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-senatori">
        <ImgSection src={IMG.fonti} alt="Le Fonti medievali" overlay="bg-black/55" height="min-h-[45vh]">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <span className="text-3xl block mb-2">{'\u265F'}</span>
            <h2 className="text-3xl md:text-4xl font-black font-mono text-zinc-200 mb-3" style={{ textShadow: '0 0 15px rgba(192,192,192,0.3)' }}>
              I Senatori
            </h2>
            <p className="text-zinc-300 font-mono text-xs uppercase tracking-widest mb-4">Over 70 — La Saggezza della Valdelsa</p>
            <p className="text-white/90 max-w-lg text-sm leading-relaxed">
              Hanno costruito questa terra. Le loro storie sono il DNA di questo territorio.
              Al Covo hanno il loro spazio: le carte, il caffe, il giornale. Ma soprattutto hanno qualcuno che li ascolta.
            </p>
          </div>
        </ImgSection>

        {/* Cascata quote */}
        <ImgSection src={IMG.cascata} alt="Cascata della Valdelsa" overlay="bg-black/60" height="min-h-[25vh]">
          <div className="w-full text-center px-4">
            <p className="text-zinc-200 font-mono font-bold text-lg md:text-xl">"Ogni Senatore e una biblioteca vivente."</p>
            <p className="text-zinc-400 text-sm mt-2">Serate di racconto, trasmissione dei saperi, dialogo tra generazioni.</p>
          </div>
        </ImgSection>
      </section>

      {/* ======================================== */}
      {/* SEZIONE 5: SPAZIO INCLUSIVO */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('inclusivo') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-inclusivo">
        <ImgSection src={IMG.inclusiva} alt="Comunita inclusiva" overlay="bg-gradient-to-r from-black/75 via-black/50 to-black/30" height="min-h-[45vh]">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <span className="text-3xl block mb-2">{'\u2764'}</span>
            <h2 className="text-3xl md:text-4xl font-black font-mono mb-3" style={{ color: '#E040FB', textShadow: '0 0 15px rgba(224,64,251,0.3)' }}>
              Spazio Inclusivo
            </h2>
            <p className="text-white/90 max-w-lg text-sm leading-relaxed mb-4">
              Disabilita. Fragilita mentale. Solitudine. Malattie. Momenti difficili che capitano a chiunque.
            </p>
            <p className="font-mono font-bold text-sm" style={{ color: '#E040FB' }}>
              Non e pieta. E dignita. Non e assistenza. E appartenenza.
            </p>
          </div>
        </ImgSection>

        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="inclusivo-pillars">
            {[
              { title: 'Accessibilita', desc: 'Spazi senza barriere, attivita per ogni abilita', icon: '\u267F' },
              { title: 'Salute Mentale', desc: 'Gruppi di ascolto, supporto psicologico, zero stigma', icon: '\u2728' },
              { title: 'Anti-Solitudine', desc: 'Nessuno deve restare solo. Il Covo e la tua comunita', icon: '\uD83E\uDD1D' },
              { title: 'Percorsi Dedicati', desc: 'Laboratori creativi, arte-terapia, movimento dolce', icon: '\u2B50' },
            ].map(item => (
              <div key={item.title} className="bg-[#0A0A0A] border border-[#E040FB]/20 rounded-xl p-5 hover:border-[#E040FB]/50 transition-colors">
                <span className="text-2xl block mb-2">{item.icon}</span>
                <h4 className="font-mono font-bold text-sm text-white mb-1">{item.title}</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SEZIONE 6: SPORT PER TUTTI */}
      {/* ======================================== */}
      <section className={`transition-all duration-500 ${!isVisible('sport') ? 'opacity-30 scale-[0.98]' : ''}`} data-testid="section-sport">
        <ImgSection src={IMG.fuochi} alt="Fuochi sulla Fortezza" overlay="bg-gradient-to-l from-black/80 via-black/50 to-transparent" height="min-h-[45vh]">
          <div className="max-w-5xl mx-auto px-6 w-full text-right">
            <span className="text-3xl block mb-2">{'\u26BD'}</span>
            <h2 className="text-3xl md:text-4xl font-black font-mono mb-3" style={{ color: '#FF6B35', textShadow: '0 0 15px rgba(255,107,53,0.3)' }}>
              Sport per Tutti
            </h2>
            <p className="text-zinc-300 font-mono text-xs uppercase tracking-widest mb-4">Ogni Sera, Ogni Eta — 18:00-20:00</p>
            <p className="text-white/80 max-w-md ml-auto text-sm leading-relaxed">
              Calcetto, basket, fitness, yoga. Il corpo si muove, la mente si libera.
              Se ci sei, giochi. Dai 14 ai 90 anni.
            </p>
          </div>
        </ImgSection>

        {/* Torri colorate + sport nel territorio */}
        <ImgSection src={IMG.torriColori} alt="Torri colorate della Valdelsa" overlay="bg-black/50" height="min-h-[25vh]">
          <div className="w-full">
            <div className="flex flex-wrap justify-center gap-3 px-4">
              {['Calcetto', 'Basket 3v3', 'Pallavolo', 'Ping Pong', 'Fitness', 'Yoga', 'Camminate', 'Ginnastica Dolce', 'Tornei'].map(tag => (
                <span key={tag} className="bg-black/40 backdrop-blur-sm border border-[#FF6B35]/40 text-white font-mono text-xs px-4 py-2 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </ImgSection>
      </section>

      <Divider />

      {/* ======================================== */}
      {/* SAN GIMIGNANO PANORAMA */}
      {/* ======================================== */}
      <ImgSection src={IMG.sanGimignano} alt="Monteriggioni" overlay="bg-black/30" height="min-h-[30vh]" testId="sangimignano-break">
        <div className="w-full text-center px-4">
          <p className="text-white font-mono font-black text-lg md:text-2xl" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
            Dove il passato e il futuro si parlano. Ogni giorno.
          </p>
        </div>
      </ImgSection>

      {/* ======================================== */}
      {/* MERCHANDISING */}
      {/* ======================================== */}
      <div className="max-w-5xl mx-auto px-4 py-16" data-testid="merch-section">
        <h2 className="text-2xl md:text-3xl font-black font-mono text-white text-center mb-2">
          Merchandising &lt;/AI_covo.tech&gt;&reg;
        </h2>
        <p className="text-zinc-500 text-center text-sm font-mono mb-8">Esclusivo per gli iscritti</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-lg p-6 hover:border-[#00FF41]/40 transition-colors text-center">
            <img src={IMG.tshirt} alt="T-Shirt" className="w-full rounded-lg mb-4" />
            <p className="text-white font-mono font-bold mb-1">T-Shirt &lt;/AI_covo.tech&gt;&reg;</p>
            <p className="text-[#00FF41] text-sm font-mono font-bold">Inclusa con iscrizione</p>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-lg p-6 hover:border-[#00FF41]/40 transition-colors text-center">
            <img src={IMG.cover} alt="Cover" className="w-full rounded-lg mb-4" />
            <p className="text-white font-mono font-bold mb-1">Cover Smartphone &lt;/AI_covo.tech&gt;&reg;</p>
            <p className="text-[#00FF41] text-sm font-mono font-bold">Inclusa con iscrizione</p>
          </div>
        </div>
      </div>

      {/* ======================================== */}
      {/* CTA ISCRIZIONE con porta background */}
      {/* ======================================== */}
      <ImgSection src={IMG.porta} alt="Porta della Valdelsa" overlay="bg-black/70" height="min-h-[auto]" testId="iscrizione-cta">
        <div className="w-full max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="mb-4" style={{ animation: 'heartbeat 2s ease-in-out infinite' }}>
            <span className="font-mono font-black text-[#00FF41] text-sm" style={{ animation: 'glowPulse 3s ease-in-out infinite' }}>
              &lt;/AI_covo.tech&gt;&reg;
            </span>
          </div>
          <span className="inline-block bg-[#00FF41]/10 border border-[#00FF41]/30 rounded-full px-4 py-1 text-[#00FF41] font-mono text-xs font-bold uppercase tracking-wider mb-4">
            Circolo Ricreativo Culturale 4.0
          </span>
          <h2 className="text-2xl md:text-3xl font-black font-mono text-white mb-2">Iscrizione Annuale</h2>
          <div className="flex items-baseline justify-center gap-2 mb-6">
            <span className="text-5xl font-black font-mono text-[#00FF41]" style={{ textShadow: '0 0 20px rgba(0,255,65,0.3)' }}>19,99</span>
            <span className="text-zinc-400 font-mono text-lg">&euro;/anno</span>
          </div>
          <ul className="text-left max-w-md mx-auto space-y-2 mb-8">
            {[
              'Accesso illimitato al Covo (15:00-20:00)',
              'La Bottega — Le Arti Perdute con i Senatori',
              'Sport incluso — per tutte le eta',
              'Rione Agora_Z — la contrada GenZ',
              'Boomer & GenX Club — Digital Detox',
              'Spazio Senatori e Spazio Inclusivo',
              'T-Shirt ufficiale inclusa al primo anno',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm">
                <span className="text-[#00FF41] font-mono font-bold mt-0.5">&gt;</span>{item}
              </li>
            ))}
          </ul>
          <Link to="/dona" data-testid="iscrizione-btn" className="inline-block bg-[#00FF41] text-black font-mono font-black text-lg px-10 py-4 rounded-lg hover:bg-[#33FF66] transition-colors">
            ISCRIVITI AL COVO
          </Link>
        </div>
      </ImgSection>
    </div>
  );
};

export default DoposcuolaPage;
